import React, { useCallback, useEffect, useMemo } from "react";
import { MeshFeatureContextHook } from "../MeshFeatureContext";
import { useAnimationsController } from "../Animation/AnimationsController";
import { CollisionObject } from "./CollisionObject";
import { applyContextPresets, getUserDataProperties } from "../FeatureUtils";
import { InteractionContextPresets } from "./ContextPresets";
import { CollisionObjectProps, CollisionShape } from "./CollisionTypes";
import { useLevelController } from "../../Commands/LevelController";
import {
  CommandContext,
  executeCommands,
  executeInitializationCommands,
} from "../../Commands/CommandUtils";

export const useTrigger: MeshFeatureContextHook = (mesh, userData = {}) => {
  const { Trigger: trigger = false } = getUserDataProperties(
    userData,
    "Object"
  );
  const animationContext = useAnimationsController();
  const levelContext = useLevelController();
  const commandContext = useMemo<CommandContext>(
    () => ({
      animationContext,
      levelContext,
    }),
    [animationContext, levelContext]
  );
  const contextPresetProps = useMemo<Partial<CollisionObjectProps>>(
    () => applyContextPresets({}, userData, InteractionContextPresets),
    [userData]
  );
  const onPlayerCollide = useCallback(() => {
    if (trigger) {
      executeCommands(mesh, userData, commandContext);
    }
  }, [trigger, mesh, userData, commandContext]);

  useEffect(() => {
    if (trigger) {
      const unmountMethods = executeInitializationCommands(
        mesh,
        userData,
        commandContext
      );

      return () => {
        for (const unmountMethod of unmountMethods) {
          if (unmountMethod instanceof Function) {
            unmountMethod();
          }
        }
      };
    }
  }, [trigger, mesh, userData, commandContext]);

  return trigger ? (
    <CollisionObject
      collisionShape={CollisionShape.Trimesh}
      {...contextPresetProps}
      meshNode={mesh}
      onPlayerCollide={onPlayerCollide}
    />
  ) : undefined;
};
